var _react_refresh_temp_1, _react_refresh_temp_2, _react_refresh_temp_3, _react_refresh_temp_4;
var _react_refresh_temp_5;
_react_refresh_temp_5 = $RefreshSig$();
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { store, useAppDispatch, useAppSelector } from '../../store';
import ScreenShareElements from '../media-elements/screenshare';
import AudioElements from '../media-elements/audios';
import SharedNotepadElement from '../shared-notepad';
import Whiteboard from '../whiteboard';
import ExternalMediaPlayer from '../external-media-player';
import DisplayExternalLink from '../display-external-link';
import VideosComponent from '../media-elements/videos';
import { doRefreshWhiteboard } from '../../store/slices/whiteboard';
import { CurrentConnectionEvents, } from '../../helpers/livekit/types';
import { updateIsActiveChatPanel, updateIsActiveParticipantsPanel, } from '../../store/slices/bottomIconsActivitySlice';
import SpeechToTextService from '../speech-to-text-service';
import { useCallbackRefState } from '../whiteboard/helpers/hooks/useCallbackRefState';
import { savePageData } from '../whiteboard/helpers/utils';
const activateWebcamsViewSelector = createSelector(_react_refresh_temp_1 = (state) => state.roomSettings, (roomSettings) => roomSettings.activateWebcamsView);
$RefreshReg$(_react_refresh_temp_1, "activateWebcamsViewSelector$createSelector");
const activateSpeechServiceSelector = createSelector(_react_refresh_temp_2 = (state) => {
    var _a;
    return (_a = state.session.currentRoom.metadata) === null || _a === void 0 ? void 0 : _a.room_features.speech_to_text_translation_features;
}, (speech_to_text_translation_features) => speech_to_text_translation_features === null || speech_to_text_translation_features === void 0 ? void 0 : speech_to_text_translation_features.is_enabled);
$RefreshReg$(_react_refresh_temp_2, "activateSpeechServiceSelector$createSelector");
const isActiveSharedNotepadSelector = createSelector(_react_refresh_temp_3 = (state) => { var _a; return (_a = state.session.currentRoom.metadata) === null || _a === void 0 ? void 0 : _a.room_features.shared_note_pad_features; }, (shared_note_pad_features) => shared_note_pad_features === null || shared_note_pad_features === void 0 ? void 0 : shared_note_pad_features.is_active);
$RefreshReg$(_react_refresh_temp_3, "isActiveSharedNotepadSelector$createSelector");
const MainComponents = ({ currentConnection, isActiveWhiteboard, isActiveExternalMediaPlayer, isActiveDisplayExternalLink, isActiveScreenSharingView, }) => {
    var _a;
    _react_refresh_temp_5();
    const dispatch = useAppDispatch();
    const isRecorder = (_a = store.getState().session.currentUser) === null || _a === void 0 ? void 0 : _a.isRecorder;
    const activateWebcamsView = useAppSelector(activateWebcamsViewSelector);
    const activateSpeechService = useAppSelector(activateSpeechServiceSelector);
    const isActiveSharedNotepad = useAppSelector(isActiveSharedNotepadSelector);
    const [showVerticalVideoView, setShowVerticalVideoView] = useState(false);
    const [hasVideoElms, setHasVideoElms] = useState(false);
    const [showVideoElms, setShowVideoElms] = useState(false);
    const [isActiveScreenShare, setIsActiveScreenShare] = useState(false);
    const [excalidrawAPI, excalidrawRefCallback] = useCallbackRefState();
    useEffect(() => {
        setHasVideoElms(currentConnection.videoSubscribersMap.size > 0);
        currentConnection.on(CurrentConnectionEvents.VideoStatus, setHasVideoElms);
        setIsActiveScreenShare(currentConnection.screenShareTracksMap.size > 0);
        currentConnection.on(CurrentConnectionEvents.ScreenShareStatus, setIsActiveScreenShare);
        return () => {
            currentConnection.off(CurrentConnectionEvents.VideoStatus, setHasVideoElms);
            currentConnection.off(CurrentConnectionEvents.ScreenShareStatus, setIsActiveScreenShare);
        };
    }, [currentConnection]);
    useEffect(() => {
        if (!isActiveScreenShare &&
            !isActiveWhiteboard &&
            !isActiveExternalMediaPlayer &&
            !isActiveDisplayExternalLink) {
            setShowVerticalVideoView(false);
        }
        else {
            setShowVerticalVideoView(true);
        }
    }, [
        isActiveScreenShare,
        isActiveWhiteboard,
        isActiveExternalMediaPlayer,
        isActiveDisplayExternalLink,
    ]);
    useEffect(() => {
        if (!activateWebcamsView) {
            setShowVideoElms(false);
            return;
        }
        setShowVideoElms(hasVideoElms);
    }, [activateWebcamsView, hasVideoElms]);
    useEffect(() => {
        const timeout = setTimeout(() => {
            if (isActiveWhiteboard) {
                dispatch(doRefreshWhiteboard());
            }
        }, 1000);
        return () => {
            clearTimeout(timeout);
        };
    }, [showVideoElms, isActiveWhiteboard, dispatch]);
    const whiteboardElm = useMemo(() => {
        var _a, _b;
        // if we disable whiteboard during that time we should collect elements from parent component
        // otherwise if we make it null then we won't be able to get last state
        if (((isActiveWhiteboard && isActiveScreenShare) || !isActiveWhiteboard) &&
            excalidrawAPI) {
            const s = store.getState();
            const isPresenter = (_b = (_a = s.session.currentUser) === null || _a === void 0 ? void 0 : _a.metadata) === null || _b === void 0 ? void 0 : _b.is_presenter;
            // we'll only do it for presenter
            if (isPresenter) {
                const lastPage = s.whiteboard.currentPage;
                savePageData(excalidrawAPI, lastPage);
            }
        }
        // for whiteboard, it's better to null not hide
        return !isActiveScreenShare && isActiveWhiteboard ? (_jsx(Whiteboard, { onReadyExcalidrawAPI: excalidrawRefCallback })) : null;
    }, [isActiveScreenShare, isActiveWhiteboard]);
    // we can't disable to show both external player & link.
    // So, external-media-player will be first priority
    const externalMediaPlayerElm = useMemo(() => {
        let classNames = 'hidden';
        if ((isActiveScreenSharingView && isActiveScreenShare) ||
            isActiveWhiteboard) {
            classNames = 'hidden';
        }
        else if (isActiveExternalMediaPlayer) {
            if (!isRecorder) {
                setTimeout(() => {
                    dispatch(updateIsActiveChatPanel(false));
                    dispatch(updateIsActiveParticipantsPanel(false));
                }, 200);
            }
            classNames =
                'Div-external-media-player w-full flex items-center justify-center';
        }
        return (_jsx("div", { className: classNames, children: _jsx(ExternalMediaPlayer, {}) }));
    }, [
        isActiveScreenSharingView,
        isActiveScreenShare,
        isActiveWhiteboard,
        isActiveExternalMediaPlayer,
    ]);
    const displayExternalLinkElm = useMemo(() => {
        let classNames = 'hidden';
        if ((isActiveScreenSharingView && isActiveScreenShare) ||
            isActiveWhiteboard ||
            isActiveExternalMediaPlayer) {
            classNames = 'hidden';
        }
        else if (isActiveDisplayExternalLink) {
            if (!isRecorder) {
                setTimeout(() => {
                    dispatch(updateIsActiveChatPanel(false));
                    dispatch(updateIsActiveParticipantsPanel(false));
                }, 200);
            }
            classNames = 'w-full';
        }
        return (_jsx("div", { className: classNames, children: _jsx(DisplayExternalLink, {}) }));
    }, [
        isActiveScreenSharingView,
        isActiveScreenShare,
        isActiveWhiteboard,
        isActiveExternalMediaPlayer,
        isActiveDisplayExternalLink,
    ]);
    // this will help to reset position, if something went wrong
    const sharedNotepadElm = useMemo(() => {
        if (isActiveSharedNotepad) {
            return _jsx(SharedNotepadElement, {});
        }
        return null;
    }, [isActiveSharedNotepad]);
    const cssClasses = useMemo(() => {
        const cssClasses = [];
        if (isActiveScreenSharingView && isActiveScreenShare) {
            cssClasses.push('middle-fullscreen-wrapper share-screen-wrapper is-share-screen-running');
            if (showVideoElms && showVerticalVideoView) {
                cssClasses.push('verticalsWebcamsActivated');
            }
        }
        else {
            if (showVideoElms && !showVerticalVideoView) {
                cssClasses.push('h-full');
            }
            else if (showVideoElms && showVerticalVideoView) {
                cssClasses.push('middle-fullscreen-wrapper h-full flex verticalsWebcamsActivated');
            }
            else {
                cssClasses.push('middle-fullscreen-wrapper h-full flex');
            }
        }
        return cssClasses.join(' ');
    }, [
        isActiveScreenSharingView,
        isActiveScreenShare,
        showVideoElms,
        showVerticalVideoView,
    ]);
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: cssClasses, children: [sharedNotepadElm, activateWebcamsView ? (_jsx(VideosComponent, { currentConnection: currentConnection, isVertical: showVerticalVideoView })) : null, isActiveScreenSharingView ? (_jsx(ScreenShareElements, { currentConnection: currentConnection })) : null, whiteboardElm, externalMediaPlayerElm, displayExternalLinkElm, activateSpeechService ? (_jsx(SpeechToTextService, { currentRoom: currentConnection.room })) : null] }), _jsx(AudioElements, { currentConnection: currentConnection })] }));
};
_react_refresh_temp_4 = MainComponents;
$RefreshReg$(_react_refresh_temp_4, "MainComponents");
_react_refresh_temp_5(MainComponents, "ugMTRkuaIy3qCPWUw6O1SVZhkds=", false, () => [useAppDispatch, useAppSelector, useAppSelector, useAppSelector, useCallbackRefState]);
export default MainComponents;
